import { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Trash2, FileText, AlertCircle, Check,
} from "lucide-react";

/**
 * Sprint 3 — Privacy & Data Center.
 *
 * Delegate-facing page for: viewing recorded consents, requesting data
 * export (sync — returns immediately), and submitting data-deletion or
 * account-deletion requests (queued for admin review).
 */
export default function PrivacyCenterPage() {
  const [consents, setConsents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/compliance/consents/me"),
        api.get("/compliance/privacy-requests/me"),
      ]);
      setConsents(c.data.items);
      setRequests(r.data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const requestExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.post("/compliance/privacy-requests", {
        kind: "data_export",
        confirm: true,
        reason: "Self-service download",
      });
      // Download the snapshot as JSON
      const snap = data.snapshot;
      if (snap) {
        const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `diplomexa-data-export-${data.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Your data has been exported and downloaded.");
      } else {
        toast.success("Export queued");
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not export");
    } finally {
      setExporting(false);
    }
  };

  const submitDeletion = async () => {
    if (!confirmDelete) {
      toast.warning("Please tick the confirmation box");
      return;
    }
    try {
      await api.post("/compliance/privacy-requests", {
        kind: "data_deletion",
        confirm: true,
        reason: deleteReason || "User-initiated deletion request",
      });
      toast.success("Your deletion request has been submitted to our compliance team.");
      setShowDelete(false);
      setConfirmDelete(false);
      setDeleteReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="privacy-center-root">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> Privacy & Data Center
          </p>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight text-[#0A192F]">
            Your data, in your control.
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Download a complete copy of everything Diplomexa has stored about you,
            or request permanent deletion. We act on every request — your conference
            history is yours.
          </p>
        </div>

        {/* Export */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="privacy-export-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
              <Download className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-[#0A192F]">Download my data</p>
              <p className="text-sm text-slate-600 mt-1">
                A JSON file containing your registrations, certificates, badges,
                LMS progress, position papers, support transcripts, and consent
                history. Generated and delivered instantly.
              </p>
              <button
                type="button"
                onClick={requestExport}
                disabled={exporting}
                className="mt-3 px-4 py-2 rounded-md bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A192F] text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="privacy-export-button"
              >
                <Download className="h-4 w-4" /> {exporting ? "Preparing…" : "Download my data"}
              </button>
            </div>
          </div>
        </section>

        {/* Delete */}
        <section className="rounded-xl border border-rose-200 bg-rose-50/50 p-6" data-testid="privacy-delete-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-rose-600" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-[#0A192F]">Delete my data</p>
              <p className="text-sm text-slate-600 mt-1">
                Submit a deletion request. Our compliance team will review and
                act on your request within 30 days. Once fulfilled, your PII is
                permanently scrubbed and your account is closed.
              </p>
              {!showDelete ? (
                <button
                  type="button"
                  onClick={() => setShowDelete(true)}
                  className="mt-3 px-4 py-2 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-100 text-sm font-semibold flex items-center gap-2"
                  data-testid="privacy-delete-open"
                >
                  <Trash2 className="h-4 w-4" /> Start deletion request
                </button>
              ) : (
                <div className="mt-4 space-y-3" data-testid="privacy-delete-form">
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Optional — tell us why you're leaving (helps us improve)"
                    rows={3}
                    className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                  />
                  <label className="flex items-start gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={confirmDelete}
                      onChange={(e) => setConfirmDelete(e.target.checked)}
                      className="mt-0.5"
                      data-testid="privacy-delete-confirm"
                    />
                    I understand that this will permanently scrub my personal data
                    and close my Diplomexa account.
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={submitDeletion}
                      className="px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
                      data-testid="privacy-delete-submit"
                    >
                      Submit deletion request
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowDelete(false); setConfirmDelete(false); }}
                      className="px-4 py-2 rounded-md border border-slate-200 text-slate-700 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* My requests */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="privacy-requests-list">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold mb-3">
            My privacy requests
          </p>
          {requests.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No requests yet.</p>
          ) : (
            <div className="space-y-2">
              {requests.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-3 p-3 rounded-md border border-slate-100 bg-slate-50"
                  data-testid={`privacy-request-${r.id}`}
                >
                  <FileText className="h-4 w-4 text-[#0F4C81]" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0A192F]">
                      {r.kind.replace("_", " ")}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleString()} · {r.status}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${
                    r.status === "fulfilled" ? "text-emerald-600" :
                    r.status === "rejected" ? "text-rose-600" :
                    "text-amber-600"
                  }`}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Consent history */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="privacy-consents-list">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold mb-3">
            Consents on record
          </p>
          {consents.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No consents recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {consents.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 bg-slate-50">
                  <Check className={`h-4 w-4 ${c.accepted ? "text-emerald-600" : "text-rose-600"}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0A192F] capitalize">
                      {c.kind.replace("_", " ")} (v{c.template_version})
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(c.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <p className="text-xs text-slate-500 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
          Diplomexa never sells your data. We act on every privacy request as required by GDPR / DPDP.
        </p>
      </div>
    </DashboardLayout>
  );
}
